import { Supervisor, EmployeeTimeSheetWithDays, JobStartResponse, JobStatus, JobStatusEnum } from "../interfaces/V2Interface";
import client from './client';

/** Surface the API's `{ error }` body (e.g. the mixed Vista PR Group rejection) instead of axios's generic status text. */
const withServerMessage = (error: unknown): unknown => {
  const serverMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return serverMessage ? new Error(serverMessage) : error;
};

export const getVistaSupervisorsForPayPeriod = async (
  payPeriodID: number
): Promise<Supervisor[]> => {
  try {
    const response = await client.get(`/admin/timesheets/payrollApproved/supervisorList?payPeriodID=${payPeriodID}`);

    if (!response.data) {
      throw new Error('No supervisor data received from server');
    }

    return response.data as Supervisor[];
  } catch (error) {
    console.error('Error fetching Vista supervisors:', error);
    throw error;
  }
};

export const getVistaPayrollApprovedTimesheets = async (
  supervisorID: number,
  payPeriodID: number
): Promise<EmployeeTimeSheetWithDays[]> => {
  try {
    const response = await client.get(`/admin/timesheets/payrollApproved?payPeriodID=${payPeriodID}&supervisorID=${supervisorID}`);

    if (!response.data) {
      throw new Error('No timesheet data received from server');
    }

    if (!Array.isArray(response.data)) {
      throw new Error('Invalid timesheet data format received');
    }

    return response.data as EmployeeTimeSheetWithDays[];
  } catch (error) {
    console.error(
      `Error fetching Vista timesheets for supervisor ${supervisorID}, period ${payPeriodID}:`,
      error
    );
    throw error;
  }
};

export const generateVistaBatchExportForEmployees = async (
  supervisorID: number,
  payPeriodID: number,
  employeeIDs: string[]
): Promise<JobStartResponse> => {
  try {
    const params = { employeeIDs, supervisorID, payPeriodID };
    const response = await client.post(`/admin/riceServices/timesheets/generateBatchExportForEmployees`, params);

    if (!response.data) {
      throw new Error('No Vista batch report data received');
    }
    
    return response.data as JobStartResponse;
  } catch (error) {
    console.error('Error generating Vista batch export:', error);
    throw withServerMessage(error);
  }
};

export const generateVistaBatchExportTimeOnlyForEmployees = async (
  supervisorID: number,
  payPeriodID: number,
  employeeIDs: string[]
): Promise<JobStartResponse> => {
  try {
    const params = { employeeIDs, supervisorID, payPeriodID };
    const response = await client.post(`/admin/riceServices/timesheets/generateBatchExportTimeOnlyForEmployees`, params);

    if (!response.data) {
      throw new Error('No Vista batch report data received');
    }
    
    return response.data as JobStartResponse;
  } catch (error) {
    console.error('Error generating Vista batch export (time only):', error);
    throw withServerMessage(error);
  }
};

export const generateVistaBatchExportPieceworksOnlyForEmployees = async (
  supervisorID: number,
  payPeriodID: number,
  employeeIDs: string[]
): Promise<JobStartResponse> => {
  try {
    const params = { employeeIDs, supervisorID, payPeriodID };
    const response = await client.post(`/admin/riceServices/timesheets/generateBatchExportPieceworksOnlyForEmployees`, params);

    if (!response.data) {
      throw new Error('No Vista batch report data received');
    }
    
    return response.data as JobStartResponse;
  } catch (error) {
    console.error('Error generating Vista batch export (pieceworks only):', error);
    throw error;
  }
};

export const getJobStatus = async (jobID: number): Promise<JobStatus> => {
  try {
    const response = await client.get(`/admin/riceServices/timesheets/jobStatus/${jobID}`);

    if (!response.data) {
      throw new Error('No job status data received');
    }
    
    return response.data as JobStatus;
  } catch (error) {
    console.error('Error getting job status:', error);
    throw error;
  }
};

// V2: Single endpoint with exportType. Comment out live URL and use test URL to test.
const GENERATE_BATCH_EXPORT_V2_PATH = '/admin/riceServices/timesheets/generateBatchExportV2';
//const GENERATE_BATCH_EXPORT_V2_PATH = '/admin/riceServices/timesheets/generateBatchExportV2Test';

export type VistaExportTypeV2 = 'time' | 'pieceworks' | 'both';

export const generateBatchExportV2ForEmployees = async (
  supervisorID: number,
  payPeriodID: number,
  employeeIDs: string[],
  exportType: VistaExportTypeV2
): Promise<JobStartResponse> => {
  try {
    const params = { employeeIDs, supervisorID, payPeriodID, exportType };
    const response = await client.post(GENERATE_BATCH_EXPORT_V2_PATH, params);

    if (!response.data) {
      throw new Error('No Vista batch report data received');
    }

    return response.data as JobStartResponse;
  } catch (error) {
    console.error('Error generating Vista batch export V2:', error);
    throw withServerMessage(error);
  }
};

const waitFor = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Poll a Vista export job to a terminal state.
 *
 * Resolves with the final `JobStatus` for BOTH outcomes — COMPLETED and FAILED — and
 * leaves the caller to decide what that means. A failed export is a screen to render,
 * not an exception to throw: the batch usually exists in Vista either way, and the
 * per-employee detail the operator needs is on the payload of a "failed" job just as
 * much as a successful one.
 *
 * Replaces `VistaBatchExportManager.pollJobStatus` for the v2 modal. That one is a
 * bare `setInterval` with no abort, whose ticks overlap if a poll runs long, and whose
 * completion check both mis-parenthesises its null-guard (`a && (b && c) || (d && e)`,
 * so a null `resultData` throws instead of falling through) and rejects a perfectly
 * good time-only export because its piece count is legitimately 0. This is the shape
 * the rest of the board already uses for job polling — see `src/api/timesheet.ts`.
 */
export const pollVistaJob = async (
  jobID: number,
  onProgress?: (status: JobStatus) => void,
  signal?: AbortSignal,
): Promise<JobStatus> => {
  for (;;) {
    if (signal?.aborted) throw new DOMException('Vista export polling aborted', 'AbortError');

    const status = await getJobStatus(jobID);
    if (signal?.aborted) throw new DOMException('Vista export polling aborted', 'AbortError');
    onProgress?.(status);

    if (status.status === JobStatusEnum.COMPLETED || status.status === JobStatusEnum.FAILED) {
      return status;
    }
    await waitFor(5000);
  }
};

export const exportJobStatusToExcel = async (jobID: number): Promise<void> => {
  try {
    const response = await client.get(`/admin/riceServices/timesheets/jobStatus/${jobID}/export`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `VistaExportStatus-${jobID}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting job status to Excel:', error);
    throw error;
  }
};

export const generateBatchTaskCSVForEmployees = async (
  supervisorID: number,
  payPeriodID: number,
  employeeIDs: string[],
  year: number,
  weekNumber: number,
  altSupervisorID: string
): Promise<void> => {
  try {
    const params = { employeeIDs, supervisorID, payPeriodID };
    const response = await client.post(`/admin/riceServices/timesheets/generateBatchTaskCSVForEmployees`, params, {
      responseType: 'blob' // Important: Set responseType to blob to handle CSV file download
    });

    // Create a blob URL for the downloaded file
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);

    // Create a temporary link element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = `Pieces-${year}-Wk${weekNumber}-${altSupervisorID}.xlsx`; // Set the filename for download
    document.body.appendChild(link);
    link.click();

    // Clean up: remove the link and revoke the blob URL
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating CSV file:', error);
    throw error;
  }
}; 